import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../constants/api';
import { notification } from 'antd';
import AutoCompleteInput from './AutoCompleteInput';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { decodeToken } from '../utils/TokenUtils';
const DataEntryTable = ({
  data,
  reportHeader,
  reportFooter,
  link,
  token,
  executionTime,
  setData,
}) => {
  const [columns, setColumns] = useState([]);
  const [data2, setData2] = useState([]);
  const { t } = useTranslation();
  // Step 1: Define the state to track which rows have been processed
  const [processedRows, setProcessedRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showApiUpdateButton, setShowApiUpdateButton] = useState(false);
  // Initial state with the original data passed as a prop
  const [originalData, setOriginalData] = useState(data);
  const [filteredData, setFilteredData] = useState(data); // Filtered data to show based on search query
  const [failedData, setFailedData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const userlevel = decodeToken(token, 'userLevel');

  const updateApiValues = async () => {
    setLoading(true);

    const successfulRows = [];
    const failedRows = [];

    try {
      for (let i = 0; i < filteredData.length; i++) {
        const row = filteredData[i];
        const updatedRow = { ...row };
        const fetchPromises = [];
        let expression;

        for (const columnKey in row) {
          const parts = columnKey.split('~');

          if (parts[2] === 'api_url' && parts.length >= 3) {
            const targetKey = parts[3];
            const targetKeyExp = parts[4];

            const originalUrl = row[columnKey];
            //expression = '5*6=7';
            // console.log('expression', expression);
            fetchPromises.push(
              axios
                .get(`${API_URL}/${ENDPOINTS.SYS}/proxy`, {
                  params: { url: originalUrl },
                  timeout: 5000,
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                })
                .then((response) => {
                  const newValue = response.data?.[targetKey];
                  // console.log(
                  //   'newValue:',
                  //   newValue,
                  //   'response.data:',
                  //   response.data
                  // );
                  if (newValue !== undefined) {
                    const targetColumn = Object.keys(row).find(
                      (k) => k.split('~')[3] === targetKey
                    );

                    if (targetColumn) {
                      updatedRow[targetColumn] = newValue;
                    }
                  } else {
                    // ✅ If targetKey not found, treat as error
                    // console.log(
                    //   `Target key "${targetKey}" missing from response for ${originalUrl}`
                    // );
                    updatedRow[`error_${targetKey}`] = true;
                  }
                })
                .catch((err) => {
                  console.log(`Failed to fetch ${originalUrl}`, err.message);
                  updatedRow[`error_${targetKey}`] = true;
                })
            );
          }
        }

        await Promise.allSettled(fetchPromises);

        // ✅ After API fetches, calculate using your function
        // calculateExpression(
        //   expression, // you should define it before
        //   i, // current row index
        //   updatedRow, // current row
        //   filteredData, // original data or updatedData
        //   setFilteredData, // setter
        //   true // supportNegative maybe true (or your flag)
        // );
        const hasError = Object.keys(updatedRow).some((key) =>
          key.startsWith('error_')
        );

        if (hasError) {
          failedRows.push(updatedRow);
        } else {
          successfulRows.push(updatedRow);
        }
      }

      setFilteredData(successfulRows);
      setOriginalData(successfulRows);

      setFailedData(failedRows);
    } catch (error) {
      console.error('Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  };

  // console.log('filteredData:', filteredData);

  // Create a ref for the input element
  const inputRef = useRef(null);

  useEffect(() => {
    // Fetch column definitions and data from the API
    const columns = Object.keys(data[0]);

    //columsn with dropdown
    const cols = columns.map((col) => {
      const parts = col.split('~');
      const columnName = col;
      const header = parts[0]; // Display label
      const key = parts[1] || parts[0]; // Key used for row data
      const type = parts[2]; // 'editable', 'dropdown', 'hide'
      const inputId = parts[3]; // Used to fetch dropdown options
      const colIndex = parts[4];

      const isDropdown = type?.includes('dropdown');
      const isEditable = type === 'editable' || type === 'api_url';
      const isHidden = type === 'hide';

      return {
        columnName,
        header,
        key,
        type,
        inputId,
        colIndex,
        isDropdown,
        editable: isEditable,
        visible: !isHidden,
        dropdownOptions: isDropdown ? [] : null,
      };
    });

    setColumns(cols);

    // check if has api_url column and show button to call update api url
    if (data?.length > 0) {
      const firstRow = data[0];
      const hasApiUrlColumn = Object.keys(firstRow).some(
        (key) => key.split('~')[2] === 'api_url'
      );
      setShowApiUpdateButton(hasApiUrlColumn);
      // console.log('button shows: ', hasApiUrlColumn);
    } else {
      // console.log('button not shows');
    }

    const fetchAllDropdowns = async (cols) => {
      for (const col of cols) {
        if (col.isDropdown && col.inputId) {
          // console.log('col:', col);
          const url = `${API_URL}/${ENDPOINTS.SYS}/get-dropdown/${col.inputId}`;

          try {
            const response = await axios.get(url, {
              headers: {
                Authorization: token ? `Bearer ${token}` : '',
              },
            });
            const options = response.data;
            //console.log('url:', url, 'response:', response.data);

            setColumns((prevColumns) =>
              prevColumns.map((c) =>
                c.key === col.key ? { ...c, dropdownOptions: options } : c
              )
            );
          } catch (err) {
            console.error(`Dropdown fetch failed for ${col.inputId}`, err);
          }
        }
      }
    };

    if (cols.length) {
      // console.log('length is true');
      fetchAllDropdowns(cols);
    } else {
      // console.log('length is false');
    }
  }, []);

  const [search, setSearch] = useState('');

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(e.target.value);

    // Filter data based on the search query
    const filtered = originalData.filter((row) =>
      Object.values(row).some((val) =>
        val?.toString().toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredData(filtered);
  };

  const [loading, setLoading] = useState(false);

  const handleEdit = async (e, rowIndex2, colData, row, colIndex) => {
    try {
      const columnName = colData.columnName;
      const updatedData = [...data];
      const updatedFiltered = [...filteredData];

      // Get new value based on input type
      let text = e.target.textContent;
      if (
        colData.type.includes('dropdown') ||
        colData.type === 'autocomplete' ||
        colData.type === 'input'
      ) {
        text = e.target.value;
      }

      // Custom function to find original index by comparing key fields
      const findMatchingRowIndex = (filteredRow, originalData) => {
        return originalData.findIndex((origRow) => {
          const keysToCompare = Object.keys(filteredRow).filter(
            (k) => !k.includes('~options') // ignore auto-injected dropdown options
          );
          return keysToCompare.every(
            (key) => filteredRow[key] === origRow[key]
          );
        });
      };

      const rowIndex = findMatchingRowIndex(row, updatedData);
      if (rowIndex === -1) return;

      // Handle 'load-dropdown' type
      if (colData.type === 'load-dropdown') {
        try {
          setLoading(true);
          const url = `${API_URL}/${ENDPOINTS.SYS}/load-dropdown/${colData.inputId}/${text}`;
          const response = await axios.get(url, {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
          });

          const newOptions = response.data.results;

          const targetColIndex = parseInt(colData.colIndex, 10);
          const optionsKey = `${columns[targetColIndex].columnName}~options`;

          // Apply to both full and filtered data
          updatedData[rowIndex][optionsKey] = newOptions;
          updatedFiltered[rowIndex2][optionsKey] = newOptions;
        } catch (error) {
          console.error('Error fetching dropdown:', error);
        } finally {
          setLoading(false);
        }
      }

      // Update the value in both data and filteredData
      updatedData[rowIndex][columnName] = text;
      updatedFiltered[rowIndex2][columnName] = text;

      console.log('Updated:', columnName, ':', text);
      console.log('Updated Row:', updatedData[rowIndex]);

      setData(updatedData);
      setFilteredData(updatedFiltered);

      // Handle expressions
      const expression = columnName?.split('~')[3]?.split(',');
      const isSupportNegative = columnName?.split('~')[4] ?? '1';

      if (expression) {
        expression.forEach((expr) => {
          if (expr) {
            calculateExpression(
              expr,
              rowIndex,
              row,
              updatedData,
              setData,
              isSupportNegative
            );
          }
        });

        // Auto-submit if "autotick" is in expression
        if (expression[0]?.includes('autotick')) {
          handleSubmit(row, rowIndex);
        }
      }
    } catch (err) {
      alert(err);
      console.error('Edit error:', err);
    }
  };
  console.log('filtered Data:', filteredData);

  // function calculateExpression(
  //   expression,
  //   rowIndex,
  //   row,
  //   updatedData,
  //   setData,
  //   isSupportNegative
  // ) {
  //   if (!expression) return;

  //   const keys = Object.keys(row);
  //   let rules = expression.split(',');
  //   console.log('rule: ', rules);

  //   for (let rule of rules) {
  //     // Arithmetic expressions like 9-8=10
  //     if (!rule.includes('rule=')) {
  //       const parts = rule.split('=');
  //       const leftSide = parts[0];
  //       const rightSide = parts[1];

  //       const delimiterMatch = leftSide.match(/[-+*/]/);
  //       if (!delimiterMatch) continue;

  //       const delimiter = delimiterMatch[0];
  //       const [col1, col2] = leftSide
  //         .split(delimiter)
  //         .map((i) => parseInt(i.trim()));
  //       const col3 = parseInt(rightSide);

  //       let value1 = parseFloat(row[keys[col1]]);
  //       let value2 = parseFloat(row[keys[col2]]);

  //       if (isNaN(value1) || isNaN(value2)) continue;

  //       let result = 0;
  //       if (delimiter === '-') result = value1 - value2;
  //       else if (delimiter === '+') result = value1 + value2;
  //       else if (delimiter === '*') result = value1 * value2;
  //       else if (delimiter === '/') result = value1 / value2;

  //       if (delimiter === '-' && result < 0 && isSupportNegative === '0') {
  //         notification.error({ message: 'The difference is negative.' });
  //         return;
  //       }

  //       updatedData[rowIndex][keys[col3]] = result.toFixed(2);
  //       console.log(
  //         `Arithmetic: ${value1} ${delimiter} ${value2} = ${result.toFixed(2)} stored in ${keys[col3]}`
  //       );
  //     }
  //     // Rule expressions like rule=10<=8?'8'=12:10*11=12
  //     else if (rule.startsWith('rule=')) {
  //       let conditionPart = rule
  //         .slice(5)
  //         .replace(/[\/\\]/g, '')
  //         .replace(/@/g, '?');
  //       conditionPart = conditionPart.replace(/'%''/, "?'");
  //       const ternaryMatch = conditionPart.match(/(.+?)\?(.+?)\:(.+)/);
  //       console.log('condition part ', conditionPart);
  //       if (!ternaryMatch) {
  //         console.error('Invalid rule format:', conditionPart);
  //         continue;
  //       }

  //       const [_, conditionRaw, thenExpr, elseExpr] = ternaryMatch;

  //       // Replace only numbers with 2 or more digits (>=10) as indexes; single-digit numbers stay literal
  //       const condition = conditionRaw.replace(/\b\d{2,}\b/g, (m) => {
  //         const index = parseInt(m);
  //         const val = parseFloat(row[keys[index]]);
  //         return isNaN(val) ? 0 : val;
  //       });

  //       let selectedExpr = '';
  //       try {
  //         const conditionResult = eval(condition);
  //         selectedExpr = conditionResult ? thenExpr : elseExpr;
  //         console.log(
  //           `Condition "${conditionRaw}" evaluated to ${conditionResult}. Selected expression: ${selectedExpr}`
  //         );
  //       } catch (e) {
  //         console.error('Error evaluating condition:', condition);
  //         continue;
  //       }

  //       // Parse selectedExpr like '8'=12 or 10*11=12
  //       const assignMatch = selectedExpr.match(/(.+?)=(\d+)/);
  //       if (!assignMatch) {
  //         console.error('Invalid assignment expression:', selectedExpr);
  //         continue;
  //       }

  //       const expr = assignMatch[1].trim();
  //       const assignIndex = parseInt(assignMatch[2]);

  //       let result;
  //       if (expr.startsWith("'") && expr.endsWith("'")) {
  //         // It's a string literal; convert to number if possible
  //         const literalStr = expr.slice(1, -1);
  //         const literalNum = Number(literalStr);
  //         result = isNaN(literalNum) ? literalStr : literalNum;
  //         console.log(
  //           `Assigning literal "${result}" (converted from ${expr}) to ${keys[assignIndex]}`
  //         );
  //       } else {
  //         // Math expression like 10*11
  //         const mathMatch = expr.match(/^(\d+)\s*([\+\-\*\/])\s*(\d+)$/);
  //         if (!mathMatch) {
  //           console.error('Invalid math expression:', expr);
  //           continue;
  //         }

  //         const [__, op1, operator, op2] = mathMatch;
  //         const v1 = parseFloat(row[keys[parseInt(op1)]]);
  //         const v2 = parseFloat(row[keys[parseInt(op2)]]);
  //         if (isNaN(v1) || isNaN(v2)) {
  //           console.error(`Values for math expression invalid: ${v1}, ${v2}`);
  //           continue;
  //         }

  //         switch (operator) {
  //           case '+':
  //             result = v1 + v2;
  //             break;
  //           case '-':
  //             result = v1 - v2;
  //             break;
  //           case '*':
  //             result = v1 * v2;
  //             break;
  //           case '/':
  //             result = v2 !== 0 ? v1 / v2 : 0;
  //             break;
  //           default:
  //             console.error('Unknown operator:', operator);
  //             continue;
  //         }
  //         result = Number(result.toFixed(2));
  //         console.log(
  //           `Calculated math expression: ${v1} ${operator} ${v2} = ${result}`
  //         );
  //       }

  //       updatedData[rowIndex][keys[assignIndex]] = result;
  //       console.log(
  //         `Assigned result "${result}" to column "${keys[assignIndex]}"`
  //       );
  //     }
  //   }
  //   const ratesKey = keys.find((k) =>
  //     k.toLowerCase().includes('rates~rates~hide')
  //   );
  //   if (ratesKey) {
  //     const rateKey = keys.find((k) => k.toLowerCase().includes('rate~rate'));
  //     const usageKey = keys.find((k) => k.toLowerCase().includes('usage'));
  //     const amountKey = keys.find((k) =>
  //       k.toLowerCase().includes('amount~amount')
  //     );
  //     const usageValue = parseFloat(updatedData[rowIndex][usageKey] || 0);
  //     const ratesStr = updatedData[rowIndex][ratesKey] || '';
  //     const rate = pickRate(ratesStr, usageValue);
  //     let result = 0;

  //     const ruleExprRaw =
  //       rules.find((r) => r.startsWith('rule='))?.slice(5) || '';
  //     const ruleExpr = ruleExprRaw.replace(/'%''/, '?');
  //     const ruleMatch = ruleExpr.match(
  //       /(\d+)\s*<=\s*(\d+)\s*\?['"]?(\d+)['"]?/
  //     );
  //     console.log('rule expre: ', ruleExpr);
  //     if (ruleMatch) {
  //       const threshold = parseFloat(ruleMatch[2]);
  //       const assignValue = parseFloat(ruleMatch[3]);

  //       if (usageValue <= threshold || rate === 0) {
  //         // apply the rule if usage <= threshold or rate = 0
  //         result = assignValue;
  //       } else {
  //         result = usageValue * rate;
  //       }
  //     } else {
  //       result = rate > 0 ? usageValue * rate : 0;
  //     }

  //     updatedData[rowIndex][amountKey] = Number(result.toFixed(2));
  //     updatedData[rowIndex][rateKey] = Number(rate.toFixed(2));
  //   }
  //   setData([...updatedData]);
  // }

 function calculateExpression(
  expression,
  rowIndex,
  row,
  updatedData,
  setData,
  isSupportNegative
) {
  if (!expression) return;

  const keys = Object.keys(row);
  const rules = expression.split(',');

  for (let rule of rules) {
    if (rule.startsWith('rule=')) continue;

    const [left, right] = rule.split('=');
    if (!left || right === undefined) continue;

    const opMatch = left.match(/[-+*/]/);
    if (!opMatch) continue;

    const op = opMatch[0];
    const [i1, i2] = left.split(op).map(v => Number(v.trim()));
    const target = Number(right);

    const v1 = Number(updatedData[rowIndex][keys[i1]]) || 0;
    const v2 = Number(updatedData[rowIndex][keys[i2]]) || 0;

    let result = 0;
    switch (op) {
      case '+': result = v1 + v2; break;
      case '-': result = v1 - v2; break;
      case '*': result = v1 * v2; break;
      case '/': result = v2 ? v1 / v2 : 0; break;
    }

    if (op === '-' && result < 0 && isSupportNegative === '0') {
      notification.error({ message: 'The difference is negative.' });
      return;
    }

    updatedData[rowIndex][keys[target]] = Number(result.toFixed(2));
  }
  const ratesKey  = keys.find(k => k.toLowerCase().includes('rates~rates~hide'));
  const rateKey   = keys.find(k => k.toLowerCase().includes('rate~rate'));
  const usageKey  = keys.find(k => k.toLowerCase().includes('usage'));
  const amountKey = keys.find(k => k.toLowerCase().includes('amount~amount'));

  let finalAmount;

  if (ratesKey && usageKey && amountKey && rateKey) {
    const usage = Number(updatedData[rowIndex][usageKey]) || 0;
    const rate  = pickRate(updatedData[rowIndex][ratesKey] || '', usage);

    finalAmount = rate > 0 ? usage * rate : 0;
    updatedData[rowIndex][rateKey] = Number(rate.toFixed(2));
    updatedData[rowIndex][amountKey] = Number(finalAmount.toFixed(2));
  }

  const ruleRaw = rules.find(r => r.startsWith('rule='))?.slice(5);
  if (ruleRaw) {
    const normalized = ruleRaw.replace(/'%'/g, '?');

    const ternary = normalized.match(/(.+?)\?(.+?)\:(.+)/);
    if (ternary) {
      const [, conditionRaw, thenExpr, elseExpr] = ternary;

      const conditionParts = conditionRaw.split(/\s*(?:&&|\|\|)\s*/);
      const logicalOps = conditionRaw.match(/&&|\|\|/g) || [];

      const results = conditionParts.map(p => {
        const m = p.match(/^(\d+)\s*(<=|>=|==|<|>)\s*(\d+)$/);
        if (!m) return false;

        const [, idx, op, staticVal] = m;
        const leftVal  = Number(updatedData[rowIndex][keys[idx]]) || 0;
        const rightVal = Number(staticVal);

        switch (op) {
          case '<=': return leftVal <= rightVal;
          case '>=': return leftVal >= rightVal;
          case '<':  return leftVal <  rightVal;
          case '>':  return leftVal >  rightVal;
          case '==': return leftVal == rightVal;
          default: return false;
        }
      });

      let conditionResult = results[0];
      logicalOps.forEach((op, i) => {
        conditionResult =
          op === '&&'
            ? conditionResult && results[i + 1]
            : conditionResult || results[i + 1];
      });

      const selected = conditionResult ? thenExpr : elseExpr;
      const assignMatch = selected.match(/(.+?)=(\d+)/);

      if (assignMatch) {
        let expr = assignMatch[1].replace(/['"]/g, '');
        const targetIdx = Number(assignMatch[2]);
        let value = 0;

        const math = expr.match(/^(\d+)\s*([\+\-\*\/])\s*(\d+)$/);
        if (math) {
          const [, i1, op, i2] = math;
          const v1 = Number(updatedData[rowIndex][keys[i1]]) || 0;
          const v2 = Number(updatedData[rowIndex][keys[i2]]) || 0;

          switch (op) {
            case '+': value = v1 + v2; break;
            case '-': value = v1 - v2; break;
            case '*': value = v1 * v2; break;
            case '/': value = v2 ? v1 / v2 : 0; break;
          }
        } else {
          value = Number(expr);
        }

        updatedData[rowIndex][keys[targetIdx]] = Number(value.toFixed(2));
      }
    }
  }

  setData([...updatedData]);
}




  function pickRate(rateStr, value) {
    if (!rateStr) return 0;
    return (
      rateStr
        .split('|')
        .map((r) => {
          const [rate, range] = r.split('*');
          const [min, max] = range.split('-').map(Number);
          return { rate: parseFloat(rate), min, max };
        })
        .find((r) => value >= r.min && value <= r.max)?.rate || 0
    );
  }

  // console.log('rowData:', rowData);
  const handleSubmit = async (row, rowIndex) => {
    const filteredRow = Object.keys(row).reduce((acc, key) => {
      const parts = key.split('~');

      // Skip if the last part is "options"
      if (parts[parts.length - 1] === 'options') return acc;

      // Only include keys that have a valid second part (the column name)
      if (parts.length >= 2) {
        acc[parts[1]] = row[key]; // Use the column name part as the key
      }
      return acc;
    }, {});

    const route = filteredRow.api_route;
    delete filteredRow.api_route;
    console.log(
      'row:',
      row,
      'Filtered row for submission:',
      filteredRow,
      'route:',
      route
    );
    var insertUrl = `${API_URL}/${ENDPOINTS.SYS}/create/${route}`;

    const response = await axios.post(insertUrl, filteredRow, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    });
     if (userlevel === 'developer') {
            console.log(
      'url:',
      insertUrl,
      'request:',
      filteredRow,
      'response:',
      response.data
    );
    }
    const { success, message } = response.data;
    if (success === 1) {
      setProcessedRows((prev) => [...prev, rowIndex]); // Track the processed row by ID
      // setProcessedRowsResponse((prev) => [...prev, rowIndex]); // Track the processed row by ID

      // Remove the row from the original data
      const updatedData = originalData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );

      // Update the original data
      setOriginalData(updatedData);

      // If the removed row is in the filteredData, we should also update filteredData
      const updatedFilteredData = filteredData.filter(
        (dataRow) => JSON.stringify(dataRow) !== JSON.stringify(row)
      );

      // Update the filtered data as well (it can stay in sync with the original data)
      setFilteredData(updatedFilteredData);

      // Focus the input field when this event is triggered
      // if (inputRef.current) {
      //   inputRef.current.focus();
      // }
      setSearch('');
      notification.success({ message: message });
    } else {
      notification.error({ message: message });
    }
  };

  const renderCell = ({ col, row, rowIndex, colIndex, handleEdit }) => {
    const value = row[col.columnName] ?? '';


    // Autocomplete
    if (col.type === 'autocomplete') {
      return (
        <AutoCompleteInput
          col={col}
          value={value}
          rowIndex={rowIndex}
          colIndex={colIndex}
          onChange={(id, name) => {
            handleEdit({ target: { value: id } }, rowIndex, col, row, colIndex);
            // row[col.columnName] = name;
          }}
        />
      );
    }

    // Dropdown
    if (col.isDropdown) {
      const options =
        row[`${col.columnName}~options`] || col.dropdownOptions || [];
      return (
        <select
          className="form-select form-select-sm"
          value={value}
          onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        >
          <option value="">-- Select --</option>
          {options.map((opt, i) => (
            <option key={i} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
      );
    }

    // Standard input
    if (col.type === 'input' && col.inputId) {
      return (
        <input
          type={col.inputId}
          className="form-control form-control-sm"
          value={value}
          onChange={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        />
      );
    }

    // Editable content
    if (col.editable) {
      return (
        <div
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => handleEdit(e, rowIndex, col, row, colIndex)}
        >
          {value}
        </div>
      );
    }

    // Non-editable: detect URLs and make them clickable
    if (
      typeof value === 'string' &&
      (value.includes('http://') || value.includes('https://'))
    ) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      );
    }

    // Default: just show value
    return value;
  };

  // console.log(
  //   'data length: ',
  //   data?.length,
  //   ' filtered length: ',
  //   filteredData.length
  // );

  // console.log('filtered data:', filteredData);

  const exportToExcel = (data, title) => {
    // Step 1: Map data to have only first part of keys
    const mappedData = data.map((row) => {
      const newRow = {};
      for (const key in row) {
        const firstPart = key.split('~')[0]; // get the first part before ~
        newRow[firstPart] = row[key];
      }
      return newRow;
    });

    // Step 2: Create sheet
    const ws = XLSX.utils.json_to_sheet(mappedData);

    // Step 3: Create workbook and export
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'data');
    XLSX.writeFile(wb, title + '.xlsx');
  };
  // ✅ Filter a row: remove ~options keys & extract proper keys
  const filterRow = (row) => {
    return Object.keys(row).reduce((acc, key) => {
      const parts = key.split('~');
      if (parts[parts.length - 1] === 'options') return acc;
      if (parts.length >= 2) {
        acc[parts[1]] = row[key];
      }
      return acc;
    }, {});
  };
  const handleSubmitAll = async () => {
    for (let rowIndex of selectedRows) {
      console.log('row ', rowIndex, ':', originalData[rowIndex]);
      await handleSubmit(originalData[rowIndex], rowIndex);
    }

    notification.success({ message: 'Selected rows submitted successfully' });
    setSelectedRows(new Set()); // Clear selection after submit
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Toggle direction if the same column is clicked again
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' }; // Default to ascending when new column clicked
    });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal === bVal) return 0;
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [filteredData, sortConfig]);

  return (
    <div className=" ">
      <div className="row">
        <div className="col-9">
          {' '}
          {showApiUpdateButton && (
            <button
              onClick={updateApiValues}
              className="btn btn-danger"
              disabled={loading}
            >
              {loading ? '⏳ Updating...' : '🔄 Update API Values'}
            </button>
          )}
          <button
            onClick={() => exportToExcel(filteredData, `${link.page_title}`)}
            className="btn btn-primary"
          >
            Export Excel
          </button>
          {failedData && failedData.length > 0 && (
            <button
              onClick={() =>
                exportToExcel(failedData, `${link.page_title}-failed`)
              }
              className="btn btn-danger"
            >
              Export Errors {failedData?.length}
            </button>
          )}
        </div>
        <div className="col-3">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            className="form-control mb-3"
            style={{ maxWidth: '300px' }} // Optional: Limit max width for better alignment
            ref={inputRef} // Attach the ref here
          />
        </div>
      </div>
      <table className="table table-bordered">
        <thead className="table-dark">
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectedRows.size === filteredData.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRows(new Set(filteredData.map((_, i) => i)));
                  } else {
                    setSelectedRows(new Set());
                  }
                }}
              />
            </th>

            {/* {columns
              .filter((col) => col.visible && col.key !== 'color') // Ignore "color" column
              .map((col) => (
                <th key={col.key}>{col.header}</th>
              ))} */}
            {columns
              .filter((col) => col.visible && col.key !== 'color') // Ignore "color" column
              .map((col) => (
                <th
                  key={col.key}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort(col.key)}
                >
                  {t(col.header)}
                  {sortConfig.key === col.key
                    ? sortConfig.direction === 'asc'
                      ? ' ▲'
                      : ' ▼'
                    : null}
                </th>
              ))}
            <th>
              {selectedRows.size > 0 && (
                <button
                  className="btn btn-primary mb-2"
                  onClick={handleSubmitAll}
                >
                  Submit Selected Rows ({selectedRows.size})
                </button>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData &&
            sortedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={
                  row.color ? `row-color-${row.color.trim().toLowerCase()}` : ''
                }
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.has(rowIndex)}
                    onChange={() => {
                      setSelectedRows((prev) => {
                        const updated = new Set(prev);
                        if (updated.has(rowIndex)) {
                          updated.delete(rowIndex);
                        } else {
                          updated.add(rowIndex);
                        }
                        return updated;
                      });
                    }}
                  />
                </td>
                {columns
                  .filter((col) => col.visible && col.key !== 'color')
                  .map((col, colIndex) => (
                    <td
                      key={col.key}
                      style={
                        col.editable ||
                        col.isDropdown ||
                        col.type === 'autocomplete'
                          ? { backgroundColor: '#f9f9f9' }
                          : {}
                      }
                    >
                      {renderCell({ col, row, rowIndex, colIndex, handleEdit })}
                    </td>
                  ))}

                <td>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleSubmit(row, rowIndex)}
                  >
                    ✅
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {executionTime !== null && (
        <p>
          Request executed in: {executionTime.toFixed(2)} sec - Total rows:
          {filteredData && filteredData.length} rows
        </p>
      )}
    </div>
  );
};

export default DataEntryTable;
